/**
 * Rate Connect utility functions
 * Campaign type handling, date ranges, and budget calculations
 */

/** Campaign mode constants */
export const CAMPAIGN_MODES = {
  BOTH: 'both',
  CPA: 'cpa',
  CPC: 'cpc',
  NET_CPA: 'netcpa',
  UNKNOWN: 'unknown',
} as const

/** Union type of campaign modes */
export type CampaignMode = typeof CAMPAIGN_MODES[keyof typeof CAMPAIGN_MODES]

/** Period stats with campaign types */
export interface PeriodStats {
  campaignTypes: string[]
  [key: string]: unknown
}

/** Campaign with aggregate types */
export interface Campaign {
  campaignTypes: string[]
  [key: string]: unknown
}

/** Budget recommendation info */
export interface BudgetRecommendationInfo {
  budgetMin: number
  budgetRecommendedMin: number
  budgetRecommendedMax: number
}

/** Budget status results */
export type BudgetStatus = 'belowConsumption' | 'tooLow' | 'low' | 'recommended' | 'excellent'

/** Campaign type selection option */
export interface CampaignTypeOption {
  description: string
  name: string
  id: CampaignMode
}

/**
 * Get array of dates between start and end date (inclusive)
 * @param startDate - Start date (string or Date)
 * @param endDate - End date (string or Date)
 * @returns Array of Date objects for each day in range
 */
export function getDatesInRange(startDate: string | Date, endDate: string | Date): Date[] {
  const start = new Date(startDate)
  const end = new Date(endDate)
  const dateArray: Date[] = []

  const currentDate = new Date(start)

  while (currentDate <= end) {
    dateArray.push(new Date(currentDate))
    currentDate.setDate(currentDate.getDate() + 1)
  }

  return dateArray
}

/**
 * Determine aggregate campaign type from multiple campaigns
 * @param campaigns - Array of period statistics
 * @param currentCampaignType - Current campaign type
 * @returns Aggregate campaign mode
 */
export function campaignsAggregateType(
  campaigns: PeriodStats[],
  currentCampaignType: CampaignMode
): CampaignMode {
  let hasCPC = false
  let hasCPA = false
  let campaignType = currentCampaignType

  campaigns.forEach(periodStats => {
    if (periodStats.campaignTypes.indexOf('cpa') >= 0 || periodStats.campaignTypes.indexOf('netCpa') >= 0) {
      hasCPA = true
    }
    if (periodStats.campaignTypes.indexOf('cpc') >= 0) {
      hasCPC = true
    }
  })

  if (hasCPA && hasCPC) {
    campaignType = CAMPAIGN_MODES.BOTH
  } else if (hasCPC && !hasCPA) {
    campaignType = CAMPAIGN_MODES.CPC
  } else if (hasCPA && !hasCPC) {
    campaignType = CAMPAIGN_MODES.CPA
  }

  return campaignType
}

/**
 * Infer campaign aggregate type from single campaign
 * @param campaign - Campaign object with campaignTypes array
 * @returns Inferred campaign mode
 */
export function inferCampaignAggregateType(campaign: Campaign): CampaignMode {
  if (campaign.campaignTypes.every(item => item.toLowerCase() === 'netcpa')) {
    return CAMPAIGN_MODES.NET_CPA
  } else if (campaign.campaignTypes.every(item => item === 'cpa')) {
    return CAMPAIGN_MODES.CPA
  } else if (campaign.campaignTypes.every(item => item === 'cpc')) {
    return CAMPAIGN_MODES.CPC
  } else {
    return CAMPAIGN_MODES.BOTH
  }
}

/**
 * Check if campaign is CPA or Net CPA type
 * @param campaignType - Campaign mode
 * @returns True if CPA or NET_CPA
 */
export const isCpaCampaign = (campaignType: CampaignMode): boolean =>
  campaignType === CAMPAIGN_MODES.CPA || campaignType === CAMPAIGN_MODES.NET_CPA

/**
 * Remove first occurrence of value from array (mutates array!)
 * @param arr - Array to modify
 * @param value - Value to remove
 * @returns Modified array
 */
function removeItemOnce<T>(arr: T[], value: T): T[] {
  const index = arr.indexOf(value)
  if (index > -1) {
    arr.splice(index, 1)
  }
  return arr
}

/**
 * Get available campaign types to select in setup flow
 * @param eligibleCampaignTypes - Campaign types user is eligible for
 * @param payPerStayFeatureFlag - Whether Pay Per Stay (netCpa) is enabled
 * @returns Array of selectable campaign type options
 */
export function getCampaignTypesToSelect(
  eligibleCampaignTypes: string[] = [],
  payPerStayFeatureFlag = false
): CampaignTypeOption[] {
  const allCampaignTypes = [...eligibleCampaignTypes]
  const availableCampaignTypes = eligibleCampaignTypes.includes('netCpa')
    ? removeItemOnce(allCampaignTypes, 'cpa')
    : eligibleCampaignTypes

  const campaignTypes: Record<string, CampaignTypeOption> = {
    cpc: {
      description: payPerStayFeatureFlag ? 'rc_pay_per_click_setup_flow_description' : 'rc_pay_per_click_description',
      name: 'rc_pay_per_click',
      id: CAMPAIGN_MODES.CPC,
    },
    cpa: {
      description: payPerStayFeatureFlag ? 'rc_pay_per_booking_setup_flow_description' : 'rc_setup_cpa_text_new',
      name: 'rc_pay_per_booking',
      id: CAMPAIGN_MODES.CPA,
    },
  }

  if (payPerStayFeatureFlag) {
    campaignTypes['netCpa'] = {
      description: 'rc_pay_per_stay_setup_flow_description',
      name: 'rc_pay_per_stay',
      id: CAMPAIGN_MODES.NET_CPA,
    }
  }

  return availableCampaignTypes.reduce<CampaignTypeOption[]>((reducedCampaignTypes, currentEligibleCampaignType) => {
    if (Object.prototype.hasOwnProperty.call(campaignTypes, currentEligibleCampaignType)) {
      reducedCampaignTypes.push(campaignTypes[currentEligibleCampaignType])
    }

    return reducedCampaignTypes
  }, [])
}

/**
 * Get budget status based on consumption and recommendations
 * @param budgetRecommendationInfo - Budget recommendation thresholds
 * @param inputBudget - User's input budget amount
 * @param budgetConsumed - Already consumed budget
 * @returns Budget status category
 */
export function getBudgetStatus(
  budgetRecommendationInfo: BudgetRecommendationInfo | null | undefined,
  inputBudget: number,
  budgetConsumed: number
): BudgetStatus {
  if (inputBudget > 0 && inputBudget < budgetConsumed) {
    return 'belowConsumption'
  } else if (budgetRecommendationInfo && inputBudget < budgetRecommendationInfo.budgetMin) {
    return 'tooLow'
  } else if (budgetRecommendationInfo && inputBudget < budgetRecommendationInfo.budgetRecommendedMin) {
    return 'low'
  } else if (budgetRecommendationInfo && inputBudget < budgetRecommendationInfo.budgetRecommendedMax) {
    return 'recommended'
  } else {
    return 'excellent'
  }
}